import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Status icons (docs/ux/design-language.md §7): one family, Lucide, on the 24-unit grid at stroke 2,
 * `currentColor`, drawn in a 16 px box and optically centred. Never a text glyph.
 *
 *   run      loader-circle (¾ arc), spins 1.6 s/turn while live (static under reduced motion)
 *   wait     filled diamond ◆ "Needs you" (amber)
 *   blocked  octagon-alert (red-orange); always shown with the reason in words
 *   ok       circle-check
 *   idle     circle-dashed ("Queued")
 *   fail     circle-x (a run or check that ended badly; different from blocked)
 *
 * Rows put the icon in a fixed 20 px icon column (`.vk-icol`) so titles align.
 */
const cx = (...p) => p.filter(Boolean).join(' ');
export const STATUS_KIND_LABEL = {
    run: 'Running',
    wait: 'Needs you',
    blocked: 'Blocked',
    ok: 'Done',
    idle: 'Queued',
    fail: 'Failed',
};
/** Lucide names, for parity with @viko/tokens `statusGlyphs[*].icon`. */
export const STATUS_KIND_ICON = {
    run: 'loader-circle',
    wait: 'diamond',
    blocked: 'octagon-alert',
    ok: 'circle-check',
    idle: 'circle-dashed',
    fail: 'circle-x',
};
function Paths({ kind }) {
    switch (kind) {
        case 'run':
            return _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" });
        case 'wait':
            return _jsx("path", { d: "M12 3.2 20.8 12 12 20.8 3.2 12Z", fill: "currentColor", stroke: "none" });
        case 'blocked':
            return (_jsxs(_Fragment, { children: [_jsx("path", { d: "M12 16h.01" }), _jsx("path", { d: "M12 8v4" }), _jsx("path", { d: "M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" })] }));
        case 'ok':
            return (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "m9 12 2 2 4-4" })] }));
        case 'fail':
            return (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "m15 9-6 6" }), _jsx("path", { d: "m9 9 6 6" })] }));
        default:
            return (_jsxs(_Fragment, { children: [_jsx("path", { d: "M10.1 2.182a10 10 0 0 1 3.8 0" }), _jsx("path", { d: "M13.9 21.818a10 10 0 0 1-3.8 0" }), _jsx("path", { d: "M17.609 3.721a10 10 0 0 1 2.69 2.7" }), _jsx("path", { d: "M2.182 13.9a10 10 0 0 1 0-3.8" }), _jsx("path", { d: "M20.279 17.609a10 10 0 0 1-2.7 2.69" }), _jsx("path", { d: "M21.818 10.1a10 10 0 0 1 0 3.8" }), _jsx("path", { d: "M3.721 6.391a10 10 0 0 1 2.7-2.69" }), _jsx("path", { d: "M6.391 20.279a10 10 0 0 1-2.69-2.7" })] }));
    }
}
export function StatusIcon({ kind, size = 16, label, live = kind === 'run', className, }) {
    const a11y = label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true };
    return (_jsx("svg", { className: cx('vk-si', `vk-si--${kind}`, live && kind === 'run' && 'vk-si--live', className), "data-status": kind, "data-icon": STATUS_KIND_ICON[kind], viewBox: "0 0 24 24", width: size, height: size, fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", focusable: "false", ...a11y, children: _jsx(Paths, { kind: kind }) }));
}
/** A count with its status icon and the 5 px gap (`.vk-cnt`); counts never truncate. */
export function StatusCount({ kind, n, label = STATUS_KIND_LABEL[kind], }) {
    return (_jsxs("span", { className: cx('vk-cnt', `vk-tone-${kind}`), children: [_jsx(StatusIcon, { kind: kind, live: false, label: label }), n] }));
}
//# sourceMappingURL=StatusIcon.js.map